/**
 * TransferToAIEditor
 *
 * Workflow node editor for the generic "AI Chatbot" action.
 *
 * Architecture:
 *   ① AI Config     — which LLM + the system prompt
 *   ② Assembled prompt preview — shows all 3 prompt layers in one place
 *   ③ MCP Servers   — optional tool servers
 *   ④ Specialists   — optional: other AI Configs this AI can delegate sub-topics to
 *   ⑤ Escalation    — agent transfer, SMS, timeout paths
 *   ⑥ Jump targets  — named workflow sections the AI can jump to by intent
 *   ⑦ Skill permissions — restrict which skills this node may use (filtered by subscription)
 */
import * as React from "react";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useBotProfiles from "@/hooks/useBotProfiles";
import useMcpServers from "@/hooks/useMcpServers";
import useSubscriptions from "@/hooks/useSubscriptions";
import useGlobalPrompt from "@/hooks/useGlobalPrompt";
import { Subscription } from "@/providers/SubscriptionsProvider";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";
import ChannelOverrideEditor from "@/pages/Messaging/Bots/ChannelOverrideEditor";
import SkillPermissionsSelector from "@/pages/Messaging/Integrations/SkillPermissionsSelector";
import ALL_SKILL_DEFS from "@/pages/Messaging/Integrations/allSkillDefs";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

// Human-readable names for model IDs stored in AIConfiguration.aiModel
const MODEL_DISPLAY: Record<string, string> = {
  "claude-haiku-4-5-20251001":  "Claude Haiku 4.5",
  "claude-3-5-haiku-20241022":  "Claude 3.5 Haiku",
  "claude-3-haiku-20240307":    "Claude 3 Haiku",
  "claude-sonnet-4-6":          "Claude Sonnet 4.6",
  "claude-3-5-sonnet-20240620": "Claude 3.5 Sonnet",
  "claude-opus-4-6":            "Claude Opus 4.6",
  "velaro-gpt-4o-mini":         "GPT-4o Mini",
  "velaro-gpt-4o":              "GPT-4o",
  "velaro-gpt-4":               "GPT-4 Turbo",
  "velaro-gpt-5":               "GPT-5",
  "gemini-2.0-flash":           "Gemini 2.0 Flash",
  "gemini-1.5-pro":             "Gemini 1.5 Pro",
  "velaro-qwen-2-5-72b":        "Qwen 2.5 72B",
};

// AI providers that support MCP tool calling
const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

// Maps subscription flags to the skill group prefix used in allSkillDefs
const INTEGRATION_SKILL_PREFIXES: Array<{ flag: keyof Subscription; prefix: string }> = [
  { flag: "enableShopify",    prefix: "Shopify" },
  { flag: "enableBigCommerce", prefix: "BigCommerce" },
  { flag: "enableWooCommerce", prefix: "WooCommerce" },
  { flag: "enableNetsuite",   prefix: "NetSuite" },
  { flag: "enableDynamics365", prefix: "Dynamics" },
  { flag: "enableHubSpot",    prefix: "HubSpot" },
  { flag: "enableMagento",    prefix: "Magento" },
  { flag: "enableSquare",     prefix: "Square" },
  { flag: "enableAccela",     prefix: "Accela" },
  { flag: "enableKlaviyo",    prefix: "Klaviyo" },
];
// Pre-built Set for O(1) "is this an integration skill?" checks inside filteredSkills
const ALL_INTEGRATION_PREFIXES = new Set(INTEGRATION_SKILL_PREFIXES.map(({ prefix }) => prefix));

// Integrations that have a dedicated AI node — show a hint when their skills are selected
const DEDICATED_NODE_HINTS: Record<string, string> = {
  BigCommerce: "BigCommerce AI",
  NetSuite: "NetSuite AI",
  HubSpot: "HubSpot AI",
  Dynamics: "Dynamics 365 AI",
};

export default function TransferToAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { botProfiles } = useBotProfiles();
  const location = useLocation();
  const { items: mcpServers } = useMcpServers();
  const { subscription } = useSubscriptions();
  const globalPrompt = useGlobalPrompt();
  const [showChannelOverrides, setShowChannelOverrides] = React.useState(false);
  const [showPromptPreview, setShowPromptPreview] = React.useState(false);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  // Filter skills to only those from integrations this site has enabled.
  // Skills without an integration prefix (Platform, Internal Ops) are always included.
  const filteredSkills = useMemo(() => {
    if (!subscription) return ALL_SKILL_DEFS;
    const enabledPrefixes = new Set(
      INTEGRATION_SKILL_PREFIXES
        .filter(({ flag }) => subscription[flag])
        .map(({ prefix }) => prefix)
    );
    return ALL_SKILL_DEFS.filter((s) => {
      if (!s.group) return true;
      const integrationPrefix = s.group.split(" — ")[0];
      return !ALL_INTEGRATION_PREFIXES.has(integrationPrefix) || enabledPrefixes.has(integrationPrefix);
    });
  }, [subscription]);

  // O(1) name → skill lookup for dedicatedNodeSuggestions
  const skillByName = useMemo(() => new Map(filteredSkills.map((s) => [s.name, s])), [filteredSkills]);

  // Detect if currently-permitted skills belong to an integration with a dedicated node
  const dedicatedNodeSuggestions = useMemo(() => {
    const permitted = Array.isArray(node.permittedSkills) ? node.permittedSkills : [];
    if (permitted.length === 0) return [];
    const usedPrefixes = new Set(
      permitted
        .map((name) => skillByName.get(name)?.group?.split(" — ")[0])
        .filter(Boolean) as string[]
    );
    return Object.entries(DEDICATED_NODE_HINTS).filter(([prefix]) => usedPrefixes.has(prefix));
  }, [node.permittedSkills, skillByName]);

  return (
    <>
      {/* Setup guide */}
      <AISetupGuide
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <a href="/ai-new/settings" className="underline font-medium">
              Create an AI Config first →
            </a>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt — it tells the AI how to behave and what it
              can do.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && (
          <div className="flex items-center gap-1.5 mt-1.5 text-xs">
            <span className="text-slate-400">Model:</span>
            <span className="font-medium text-slate-600">
              {MODEL_DISPLAY[selectedAiConfig.aiModel] ?? selectedAiConfig.aiModel ?? "Platform default"}
            </span>
            <a
              href={`/ai-new/settings/general/${selectedAiConfig.id}`}
              className="text-blue-500 hover:underline ml-1"
            >
              change →
            </a>
          </div>
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            This AI config has no system prompt.{" "}
            <a href={`/ai-new/settings/general/${selectedAiConfig.id}`} className="underline">
              Add a prompt →
            </a>{" "}
            to define how this AI behaves.
          </p>
        )}
        {selectedAiConfig && selectedAiConfig.prompt && (
          <div className="flex items-center gap-2 mt-1.5">
            <a
              href={`/ai-new/settings/general/${selectedAiConfig.id}`}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
            >
              Edit Prompt →
            </a>
            <button
              type="button"
              onClick={() => setShowChannelOverrides((v) => !v)}
              className="text-xs text-slate-500 hover:text-blue-600 font-medium hover:underline"
              aria-expanded={showChannelOverrides}
            >
              {showChannelOverrides ? "Hide Channel Overrides" : "Channel Overrides"}
            </button>
            <button
              type="button"
              onClick={() => setShowPromptPreview((v) => !v)}
              className="text-xs text-slate-500 hover:text-blue-600 font-medium hover:underline"
              aria-expanded={showPromptPreview}
            >
              {showPromptPreview ? "Hide Preview" : "Preview Full Prompt"}
            </button>
          </div>
        )}
      </FormGroup>

      {/* ── Step 1b: Bot Persona ── */}
      <FormGroup label="Bot Persona">
        <Select
          options={[
            { value: 0, label: "— Use site default —" },
            ...botProfiles.map((p) => ({
              value: p.id,
              label: p.name + (p.isDefault ? " (default)" : ""),
            })),
          ]}
          value={node.botProfileId ?? 0}
          onChange={(value) => onUpdate({ botProfileId: Number(value) || undefined })}
        />
        {node.botProfileId && botProfiles.find((p) => p.id === node.botProfileId) && (() => {
          const p = botProfiles.find((bp) => bp.id === node.botProfileId)!;
          const returnTo = encodeURIComponent(location.pathname + location.search);
          return (
            <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500 flex-wrap">
              {p.tone && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
                  {p.tone}
                </span>
              )}
              {p.enableVoice && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cornflower-blue-50 text-cornflower-blue-600">
                  <i className="fa fa-microphone text-[10px]" /> Voice enabled
                </span>
              )}
              <a
                href={`/bots?editId=${p.id}&returnTo=${returnTo}`}
                className="ml-auto text-cornflower-blue-500 hover:text-cornflower-blue-700 hover:underline font-medium"
              >
                Edit persona →
              </a>
            </div>
          );
        })()}
        {!node.botProfileId && (
          <p className="text-xs text-slate-400 mt-1">
            Using site default.{" "}
            <a href={`/bots?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
               className="text-cornflower-blue-500 hover:underline">
              Manage personas →
            </a>
          </p>
        )}
      </FormGroup>

      {/* ── Assembled Prompt Preview ── */}
      {showPromptPreview && selectedAiConfig && (
        <div className="mt-1 rounded-lg border border-slate-200 overflow-hidden text-xs">
          <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            Assembled prompt — what the AI actually receives
          </div>
          {/* Layer 1 */}
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Layer 1 — Global prompt
              </span>
              <a href="/account/settings/ai" className="text-[10px] text-blue-500 hover:underline ml-auto">
                Edit →
              </a>
            </div>
            {globalPrompt ? (
              <pre className="text-slate-600 whitespace-pre-wrap font-sans leading-relaxed max-h-24 overflow-y-auto">
                {globalPrompt}
              </pre>
            ) : (
              <span className="text-slate-400 italic">Not set — applies to all bots site-wide when set.</span>
            )}
          </div>
          {/* Layer 2 */}
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Layer 2 — Bot prompt ({selectedAiConfig.name})
              </span>
              <a
                href={`/ai-new/settings/general/${selectedAiConfig.id}`}
                className="text-[10px] text-blue-500 hover:underline ml-auto"
              >
                Edit →
              </a>
            </div>
            <pre className="text-slate-600 whitespace-pre-wrap font-sans leading-relaxed max-h-24 overflow-y-auto">
              {selectedAiConfig.prompt || <span className="text-slate-400 italic">No prompt set.</span>}
            </pre>
          </div>
          {/* Layer 3 */}
          <div className="px-3 py-2 bg-slate-50">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Layer 3 — Channel overrides
            </span>
            <p className="text-slate-500 mt-0.5">
              Per-channel instructions (IVR, SMS, WhatsApp, etc.) are configured via{" "}
              <button
                type="button"
                className="text-blue-500 hover:underline"
                onClick={() => { setShowChannelOverrides(true); setShowPromptPreview(false); }}
              >
                Channel Overrides
              </button>
              {" "}above and appended after Layer 2 at runtime.
            </p>
          </div>
        </div>
      )}

      {/* ── Channel Prompt Overrides ── */}
      {showChannelOverrides && selectedAiConfig && (
        <div className="mt-2 pt-3 border-t border-slate-200">
          <ChannelOverrideEditor
            aiConfigId={selectedAiConfig.id}
            botPrompt={selectedAiConfig.prompt}
            globalPrompt={globalPrompt ?? undefined}
            onClose={() => setShowChannelOverrides(false)}
          />
        </div>
      )}

      {/* ── Context Inspector (debug) ── */}
      <AIContextInspector mcpToolNames={enabledToolNames} />

      {/* ── Specialist Agents ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      {/* ── Escalation & Handoff ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>

      {/* ── Jump to Workflow Section ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>

      {/* ── Permitted Skills ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        {dedicatedNodeSuggestions.length > 0 && (
          <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <span className="font-semibold">Tip: </span>
            {dedicatedNodeSuggestions.map(([, label]) => label).join(" and ")}{" "}
            {dedicatedNodeSuggestions.length === 1 ? "has" : "have"} a dedicated AI node with
            pre-built wizard templates. The generic node is fine for mixed integrations — use the
            dedicated node only if this workflow is single-integration.
          </div>
        )}
        <SkillPermissionsSelector
          availableSkills={filteredSkills}
          permittedSkills={Array.isArray(node.permittedSkills) ? node.permittedSkills : []}
          onChange={(skills) => onUpdate({ permittedSkills: skills.length > 0 ? skills : undefined })}
        />
      </div>

      {/* ── MCP Tool Servers — advanced / subscription-gated ── */}
      {subscription?.enableMcp && (
        <McpSection
          enabledMcpServers={enabledMcpServers}
          selectedMcpIds={selectedMcpIds}
          providerUnsupported={providerUnsupported}
          onToggle={toggleMcpServer}
        />
      )}
    </>
  );
}

// ── MCP collapsible section ──────────────────────────────────────────────────

function McpSection({
  enabledMcpServers,
  selectedMcpIds,
  providerUnsupported,
  onToggle
}: {
  enabledMcpServers: ReturnType<typeof Array.prototype.filter>;
  selectedMcpIds: number[];
  providerUnsupported: boolean;
  onToggle: (id: number, checked: boolean) => void;
}) {
  const [open, setOpen] = React.useState(selectedMcpIds.length > 0);
  const activeCount = enabledMcpServers.filter((s: any) => selectedMcpIds.includes(s.id)).length;

  return (
    <div className="mt-2 pt-3 border-t border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            MCP Tool Servers
          </span>
          {activeCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
              {activeCount} active
            </span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
            Advanced
          </span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="mt-2">
          {enabledMcpServers.length === 0 ? (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2.5">
              No MCP tool servers configured.{" "}
              <a href="/integrations/mcp" className="text-blue-600 hover:underline font-medium">
                Add one under Integrations → MCP Servers →
              </a>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {providerUnsupported && (
                <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                  MCP tools require Claude or OpenAI. The selected AI config may not support them.
                </p>
              )}
              {(enabledMcpServers as any[]).map((server) => {
                const checked = selectedMcpIds.includes(server.id);
                return (
                  <label
                    key={server.id}
                    className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                      checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 mt-0.5 shrink-0"
                      checked={checked}
                      onChange={(e) => onToggle(server.id, e.target.checked)}
                      aria-label={`Enable MCP server: ${server.name}`}
                    />
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{server.name}</span>
                        {server.toolCount > 0 && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                            {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                          </span>
                        )}
                        <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                      </div>
                      {server.description && (
                        <span className="text-xs text-slate-500">{server.description}</span>
                      )}
                      {server.toolNames?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {server.toolNames.slice(0, 6).map((t: string) => (
                            <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                              {t}
                            </span>
                          ))}
                          {server.toolNames.length > 6 && (
                            <span className="text-xs text-slate-400 px-1 py-0.5">
                              +{server.toolNames.length - 6} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
